import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  Textarea,
  Badge,
  useToast,
  Spinner,
  VStack,
  Select,
  IconButton,
  Divider,
  Wrap,
  WrapItem,
  Flex,
  Heading,
  Image,
} from "@chakra-ui/react";
import {
  Minus,
  Plus,
  Trash2,
  Upload,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { useFirebaseImageUpload } from "../../store/hooks/useFirebaseImageUpload";
import { Product } from "../../types/product";
import { UploadFolder } from "../../types/upload.types";
import { ImageUploader } from "../../utils/ImageUploader";
import { storage } from "../../configs/firebase";
import GlobalSpinner from "../CustomReusables/GlobalSpinner";

type ProductFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  product?: Product | null;
  userId?: string;
  onSubmit: (payload: Partial<Product>) => Promise<void>;
};

type VariantOptionDraft = {
  id?: string;
  label: string;
  value: string;
  hex: string;
  images: string[];
  price: number | string;
  stockQty: number | string;
  sku: string;
  isActive: boolean;
};

type VariantAttributeDraft = {
  id?: string;
  name: string;
  type: "color" | "size" | "text";
  options: VariantOptionDraft[];
};

type ProductDraft = {
  title: string;
  price: number | string;
  category: string;
  description: string;
  userId?: string;
  brand: string;
  tags: string;
  stockQty: number | string;
  status: "active" | "draft" | "out_of_stock" | "archived";
  isFeatured: boolean;
  variantAttributes: VariantAttributeDraft[];
};

type PendingOptionUpload = {
  files: File[];
  previews: string[];
};

const cardsBorderRadii = "8px";

const createEmptyVariantOption = (): VariantOptionDraft => ({
  label: "",
  value: "",
  hex: "",
  images: [],
  price: "",
  stockQty: "",
  sku: "",
  isActive: true,
});

const createEmptyVariantAttribute = (): VariantAttributeDraft => ({
  name: "",
  type: "text",
  options: [createEmptyVariantOption()],
});

const EMPTY_DRAFT: ProductDraft = {
  title: "",
  price: "",
  category: "",
  description: "",
  userId: undefined,
  brand: "",
  tags: "",
  stockQty: "",
  status: "active",
  isFeatured: false,
  variantAttributes: [],
};

const makeOptionKey = (attributeIndex: number, optionIndex: number) =>
  `${attributeIndex}-${optionIndex}`;

const safeSegment = (value: string, fallback: string) =>
  String(value || fallback)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "");

const moveItem = <T,>(arr: T[], from: number, to: number) => {
  const copy = [...arr];
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
};

const normalizeIncomingImages = (product?: any): string[] => {
  if (!product) return [];
  if (Array.isArray(product.images) && product.images.length > 0) {
    return product.images.filter(Boolean);
  }
  if (product.imageUrl) return [product.imageUrl];
  return [];
};

const normalizeIncomingVariantAttributes = (
  product?: any,
): VariantAttributeDraft[] => {
  if (!product || !Array.isArray(product.variantAttributes)) return [];

  return product.variantAttributes.map((attribute: any) => ({
    id: attribute?._id,
    name: attribute?.name || "",
    type: attribute?.type || "text",
    options:
      Array.isArray(attribute?.options) && attribute.options.length > 0
        ? attribute.options.map((option: any) => ({
            id: option?._id,
            label: option?.label || "",
            value: option?.value || "",
            hex: option?.hex || "",
            images: Array.isArray(option?.images)
              ? option.images.filter(Boolean)
              : option?.imageUrl
                ? [option.imageUrl]
                : option?.thumbnail
                  ? [option.thumbnail]
                  : [],
            price: option?.price ?? "",
            stockQty: option?.stockQty ?? "",
            sku: option?.sku || "",
            isActive: option?.isActive !== false,
          }))
        : [createEmptyVariantOption()],
  }));
};

const buildDraftFromProduct = (
  product?: any,
  userId?: string,
): ProductDraft => {
  if (!product) {
    return {
      ...EMPTY_DRAFT,
      userId,
    };
  }

  return {
    title: product?.title || "",
    price: product?.price ?? "",
    category: product?.category || "",
    description: product?.description || "",
    userId: product?.userId || userId,
    brand: product?.brand || "",
    tags: Array.isArray(product?.tags) ? product.tags.join(", ") : "",
    stockQty: product?.stockQty ?? "",
    status: product?.status || "active",
    isFeatured: Boolean(product?.isFeatured),
    variantAttributes: normalizeIncomingVariantAttributes(product),
  };
};

export const ProductFormModal: React.FC<ProductFormModalProps> = ({
  isOpen,
  onClose,
  mode,
  product,
  userId,
  onSubmit,
}) => {
  const toast = useToast();
  const { theme } = useCustomTheme();
  const isDark = theme.name === "dark";

  const [draft, setDraft] = useState<ProductDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [pendingOptionUploads, setPendingOptionUploads] = useState<
    Record<string, PendingOptionUpload>
  >({});

  const {
    setUrls,
    previews,
    onPickFiles,
    removeAt,
    uploadSelected,
    isUploading,
    errorMsg,
  } = useFirebaseImageUpload({
    folder: "products" as UploadFolder,
    maxFiles: 12,
    initialUrls: normalizeIncomingImages(product),
    initialImageUrl: product?.imageUrl,
  });

  useEffect(() => {
    if (!isOpen) return;

    const incomingImages = normalizeIncomingImages(product);
    setDraft(buildDraftFromProduct(product, userId));
    setUrls(incomingImages);

    Object.values(pendingOptionUploads).forEach((entry) => {
      entry.previews.forEach((preview) => URL.revokeObjectURL(preview));
    });
    setPendingOptionUploads({});
  }, [isOpen, product, userId, setUrls]);

  useEffect(() => {
    return () => {
      Object.values(pendingOptionUploads).forEach((entry) => {
        entry.previews.forEach((preview) => URL.revokeObjectURL(preview));
      });
    };
  }, [pendingOptionUploads]);

  const baseControlStyle = {
    border: `1px solid ${theme.border}`,
    borderRadius:{cardsBorderRadii},
    bg: theme.inputBg || (isDark ? "rgba(255,255,255,0.03)" : "white"),
    color: theme.text,
    _hover: { borderColor: theme.inputBorderFocus || theme.borderStrong },
    _focus: {
      borderColor:
        theme.inputBorderFocus || theme.brand?.electricBlue || "#27b7ff",
      boxShadow: `0 0 0 1px ${
        theme.inputBorderFocus || theme.brand?.electricBlue || "#27b7ff"
      }`,
    },
  };

  const primaryButtonStyles = {
    color: theme.textOnPrimary,
    bgImage: theme.gradients?.primaryButton || theme.primary,
    _hover: {
      bgImage: theme.gradients?.primaryButtonHover || theme.primaryHover,
    },
    borderRadius: {cardsBorderRadii},
    boxShadow: theme.shadowSm,
  };

  const secondaryButtonStyles = {
    border: `1px solid ${theme.border}`,
    bg: isDark ? "rgba(255,255,255,0.03)" : "white",
    color: theme.text,
    _hover: { bg: isDark ? "rgba(255,255,255,0.06)" : "gray.50" },
    borderRadius:{cardsBorderRadii}
  };

  const onChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setDraft((prev) => ({ ...prev, [name]: value }));
  };

  const setDraftField = <K extends keyof ProductDraft>(
    key: K,
    value: ProductDraft[K],
  ) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const addVariantAttribute = () => {
    setDraft((prev) => ({
      ...prev,
      variantAttributes: [
        ...prev.variantAttributes,
        createEmptyVariantAttribute(),
      ],
    }));
  };

  const removeVariantAttribute = (attributeIndex: number) => {
    setDraft((prev) => ({
      ...prev,
      variantAttributes: prev.variantAttributes.filter(
        (_, index) => index !== attributeIndex,
      ),
    }));
  };

  const updateVariantAttribute = (
    attributeIndex: number,
    field: keyof VariantAttributeDraft,
    value: any,
  ) => {
    setDraft((prev) => ({
      ...prev,
      variantAttributes: prev.variantAttributes.map((attribute, index) =>
        index === attributeIndex ? { ...attribute, [field]: value } : attribute,
      ),
    }));
  };

  const addVariantOption = (attributeIndex: number) => {
    setDraft((prev) => ({
      ...prev,
      variantAttributes: prev.variantAttributes.map((attribute, index) =>
        index === attributeIndex
          ? {
              ...attribute,
              options: [...attribute.options, createEmptyVariantOption()],
            }
          : attribute,
      ),
    }));
  };

  const removeVariantOption = (attributeIndex: number, optionIndex: number) => {
    const optionKey = makeOptionKey(attributeIndex, optionIndex);

    setPendingOptionUploads((prev) => {
      const next = { ...prev };
      const existing = next[optionKey];
      if (existing) {
        existing.previews.forEach((preview) => URL.revokeObjectURL(preview));
      }
      delete next[optionKey];
      return next;
    });

    setDraft((prev) => ({
      ...prev,
      variantAttributes: prev.variantAttributes.map((attribute, index) => {
        if (index !== attributeIndex) return attribute;
        const nextOptions = attribute.options.filter(
          (_, idx) => idx !== optionIndex,
        );
        return {
          ...attribute,
          options:
            nextOptions.length > 0 ? nextOptions : [createEmptyVariantOption()],
        };
      }),
    }));
  };

  const updateVariantOption = (
    attributeIndex: number,
    optionIndex: number,
    field: keyof VariantOptionDraft,
    value: any,
  ) => {
    setDraft((prev) => ({
      ...prev,
      variantAttributes: prev.variantAttributes.map((attribute, index) => {
        if (index !== attributeIndex) return attribute;

        return {
          ...attribute,
          options: attribute.options.map((option, idx) =>
            idx === optionIndex ? { ...option, [field]: value } : option,
          ),
        };
      }),
    }));
  };

  const handleOptionImagesPicked = (
    attributeIndex: number,
    optionIndex: number,
    files: FileList | null,
  ) => {
    if (!files || files.length === 0) return;

    const key = makeOptionKey(attributeIndex, optionIndex);
    const newFiles = Array.from(files);
    const newPreviews = newFiles.map((file) => URL.createObjectURL(file));

    setPendingOptionUploads((prev) => ({
      ...prev,
      [key]: {
        files: [...(prev[key]?.files || []), ...newFiles],
        previews: [...(prev[key]?.previews || []), ...newPreviews],
      },
    }));
  };

  const removePendingOptionImage = (
    attributeIndex: number,
    optionIndex: number,
    imageIndex: number,
  ) => {
    const key = makeOptionKey(attributeIndex, optionIndex);

    setPendingOptionUploads((prev) => {
      const entry = prev[key];
      if (!entry) return prev;

      const previewToRemove = entry.previews[imageIndex];
      if (previewToRemove) URL.revokeObjectURL(previewToRemove);

      return {
        ...prev,
        [key]: {
          files: entry.files.filter((_, idx) => idx !== imageIndex),
          previews: entry.previews.filter((_, idx) => idx !== imageIndex),
        },
      };
    });
  };

  const removeSavedOptionImage = (
    attributeIndex: number,
    optionIndex: number,
    imageIndex: number,
  ) => {
    setDraft((prev) => ({
      ...prev,
      variantAttributes: prev.variantAttributes.map((attribute, attrIdx) => {
        if (attrIdx !== attributeIndex) return attribute;

        return {
          ...attribute,
          options: attribute.options.map((option, optIdx) => {
            if (optIdx !== optionIndex) return option;

            return {
              ...option,
              images: option.images.filter((_, idx) => idx !== imageIndex),
            };
          }),
        };
      }),
    }));
  };

  const uploadOptionFiles = async (
    uploadKey: string,
    attributeName: string,
    optionValue: string,
    files: File[],
  ) => {
    const uploadedUrls: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const path = `products/${uploadKey}/variants/${safeSegment(
        attributeName,
        "attribute",
      )}/${safeSegment(optionValue, "option")}/${Date.now()}-${i}-${file.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      uploadedUrls.push(downloadUrl);
    }

    return uploadedUrls;
  };

  const buildNormalizedVariantAttributes = async (uploadKey: string) => {
    const normalizedAttributes = [];

    for (
      let attributeIndex = 0;
      attributeIndex < draft.variantAttributes.length;
      attributeIndex++
    ) {
      const attribute = draft.variantAttributes[attributeIndex];
      if (!attribute.name.trim()) continue;

      const normalizedOptions = [];

      for (
        let optionIndex = 0;
        optionIndex < attribute.options.length;
        optionIndex++
      ) {
        const option = attribute.options[optionIndex];
        const key = makeOptionKey(attributeIndex, optionIndex);
        const pendingFiles = pendingOptionUploads[key]?.files || [];

        const uploadedUrls =
          pendingFiles.length > 0
            ? await uploadOptionFiles(
                uploadKey,
                attribute.name,
                option.value || option.label || `option-${optionIndex + 1}`,
                pendingFiles,
              )
            : [];

        const mergedImages = [...(option.images || []), ...uploadedUrls].filter(
          Boolean,
        );

        if (
          !option.label.trim() &&
          !option.value.trim() &&
          mergedImages.length === 0
        ) {
          continue;
        }

        normalizedOptions.push({
          ...(option.id ? { _id: option.id } : {}),
          label: option.label.trim() || option.value.trim(),
          value: option.value.trim() || option.label.trim(),
          hex: option.hex.trim(),
          imageUrl: mergedImages[0] || "",
          thumbnail: mergedImages[0] || "",
          images: mergedImages,
          price:
            option.price === "" || option.price === null
              ? undefined
              : Number(option.price),
          stockQty:
            option.stockQty === "" || option.stockQty === null
              ? undefined
              : Number(option.stockQty),
          sku: option.sku.trim(),
          isActive: option.isActive !== false,
        });
      }

      if (normalizedOptions.length > 0) {
        normalizedAttributes.push({
          ...(attribute.id ? { _id: attribute.id } : {}),
          name: attribute.name.trim(),
          type: attribute.type,
          options: normalizedOptions,
        });
      }
    }

    return normalizedAttributes;
  };

  const validate = () => {
    if (!draft.title.trim()) return "Title is required";
    if (!draft.category.trim()) return "Category is required";
    if (draft.price === "" || Number(draft.price) < 0)
      return "Price must be valid";

    for (const attribute of draft.variantAttributes) {
      if (!attribute.name.trim()) {
        return "Each option group must have a name";
      }

      const validOptions = attribute.options.filter(
        (option) =>
          option.label.trim() ||
          option.value.trim() ||
          (Array.isArray(option.images) && option.images.length > 0),
      );

      if (validOptions.length === 0) {
        return `Add at least one option under ${attribute.name || "the option group"}`;
      }
    }

    return null;
  };

  const moveProductImage = (from: number, to: number) => {
    if (to < 0 || to >= previews.length) return;
    const next = moveItem(previews, from, to);
    setUrls(next);
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      toast({
        title: validationError,
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const uploadKey =
        product?._id ||
        `${userId || "guest"}-${Date.now()}-${draft.title
          .replace(/\s+/g, "-")
          .toLowerCase()}`;

      const finalImageUrls = await uploadSelected(uploadKey);
      const normalizedVariantAttributes =
        await buildNormalizedVariantAttributes(uploadKey);

      const resolvedImages =
        finalImageUrls.length > 0
          ? finalImageUrls
          : Array.isArray(previews)
            ? previews.filter(Boolean)
            : [];

      const payload: Partial<Product> = {
        title: draft.title.trim(),
        description: draft.description.trim(),
        price: Number(draft.price),
        category: draft.category.trim(),
        imageUrl: resolvedImages[0] || "",
        thumbnail: resolvedImages[0] || "",
        ...(resolvedImages.length > 0
          ? { images: resolvedImages }
          : { images: [] }),
        userId: draft.userId || userId,
        brand: draft.brand.trim(),
        tags: draft.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean),
        stockQty:
          draft.stockQty === "" || draft.stockQty === null
            ? 0
            : Number(draft.stockQty),
        status: draft.status,
        isFeatured: draft.isFeatured,
        hasVariants: normalizedVariantAttributes.length > 0,
        variantAttributes: normalizedVariantAttributes,
      };

      await onSubmit(payload);

      toast({
        title: mode === "edit" ? "Product updated" : "Product created",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      onClose();
    } catch (e: any) {
      console.error(e);
      toast({
        title: "Failed to save product",
        description: e?.message || "Please try again.",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const imageCount = useMemo(() => previews.length, [previews]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      size="5xl"
      isCentered
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent
        bg={theme.surface}
        border={`1px solid ${theme.border}`}
        borderRadius={cardsBorderRadii}
        boxShadow={theme.shadowMd}
        overflow="hidden"
      >
        <Box
          h="3px"
          bg={
            theme.gradients?.primaryButton ||
            theme.brand?.electricBlue ||
            "#27b7ff"
          }
        />

        <ModalHeader color={theme.text}>
          {mode === "edit" ? "Edit Product" : "Create Product"}
        </ModalHeader>
        <ModalCloseButton color={theme.textMuted} />

        <ModalBody>
          <VStack spacing={5} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired>
                <FormLabel color={theme.textMuted}>Title</FormLabel>
                <Input
                  name="title"
                  value={draft.title}
                  onChange={onChange}
                  placeholder="Product title"
                  {...baseControlStyle}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color={theme.textMuted}>Category</FormLabel>
                <Input
                  name="category"
                  value={draft.category}
                  onChange={onChange}
                  placeholder="e.g. Electronics"
                  {...baseControlStyle}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color={theme.textMuted}>Base Price</FormLabel>
                <Input
                  name="price"
                  type="number"
                  value={draft.price}
                  onChange={onChange}
                  placeholder="0"
                  {...baseControlStyle}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={theme.textMuted}>Stock Qty</FormLabel>
                <Input
                  name="stockQty"
                  type="number"
                  value={draft.stockQty}
                  onChange={onChange}
                  placeholder="0"
                  {...baseControlStyle}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={theme.textMuted}>Brand</FormLabel>
                <Input
                  name="brand"
                  value={draft.brand}
                  onChange={onChange}
                  placeholder="Optional brand"
                  {...baseControlStyle}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={theme.textMuted}>Tags</FormLabel>
                <Input
                  name="tags"
                  value={draft.tags}
                  onChange={onChange}
                  placeholder="comma, separated, tags"
                  {...baseControlStyle}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={theme.textMuted}>Status</FormLabel>
                <Select
                  name="status"
                  value={draft.status}
                  onChange={onChange}
                  {...baseControlStyle}
                  sx={{
                    option: {
                      background: isDark ? "#1f2430" : "#fff",
                      color: isDark ? "#f7fafc" : "#1a202c",
                    },
                  }}
                >
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="out_of_stock">Out of stock</option>
                  <option value="archived">Archived</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel color={theme.textMuted}>Featured</FormLabel>
                <Select
                  value={draft.isFeatured ? "true" : "false"}
                  onChange={(e) =>
                    setDraftField("isFeatured", e.target.value === "true")
                  }
                  {...baseControlStyle}
                  sx={{
                    option: {
                      background: isDark ? "#1f2430" : "#fff",
                      color: isDark ? "#f7fafc" : "#1a202c",
                    },
                  }}
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </Select>
              </FormControl>

              <FormControl gridColumn={{ md: "1 / span 2" }}>
                <FormLabel color={theme.textMuted}>Description</FormLabel>
                <Textarea
                  name="description"
                  value={draft.description}
                  onChange={onChange}
                  placeholder="Write a product description..."
                  rows={4}
                  {...baseControlStyle}
                />
              </FormControl>

              <Box gridColumn={{ md: "1 / span 2" }}>
                <ImageUploader
                  label="Product Images (multiple)"
                  previews={previews}
                  onPickFiles={onPickFiles}
                  onRemovePreview={removeAt}
                  maxFiles={12}
                  helperText="First image = cover image. Works for create and update."
                  isUploading={isUploading}
                  errorMsg={errorMsg}
                />

                {previews.length > 0 && (
                  <Box mt={4}>
                    <Text
                      fontSize="sm"
                      fontWeight="700"
                      color={theme.text}
                      mb={3}
                    >
                      Arrange product images
                    </Text>

                    <Wrap spacing={3}>
                      {previews.map((src, index) => (
                        <WrapItem key={`${src}-${index}`}>
                          <Box
                            position="relative"
                            border={`1px solid ${theme.border}`}
                            borderRadius={cardsBorderRadii}
                            p={2}
                            bg={theme.surfaceAlt}
                          >
                            <Image
                              src={src}
                              alt={`Product image ${index + 1}`}
                              boxSize="88px"
                              objectFit="cover"
                              borderRadius={cardsBorderRadii}
                            />

                            {index === 0 && (
                              <Badge
                                position="absolute"
                                top="6px"
                                left="6px"
                                colorScheme="green"
                              >
                                Cover
                              </Badge>
                            )}

                            <HStack mt={2} spacing={1} justify="center">
                              <IconButton
                                aria-label="Move image left"
                                icon={<ChevronLeft size={14} />}
                                size="xs"
                                onClick={() =>
                                  moveProductImage(index, index - 1)
                                }
                                isDisabled={index === 0}
                                {...secondaryButtonStyles}
                              />
                              <IconButton
                                aria-label="Move image right"
                                icon={<ChevronRight size={14} />}
                                size="xs"
                                onClick={() =>
                                  moveProductImage(index, index + 1)
                                }
                                isDisabled={index === previews.length - 1}
                                {...secondaryButtonStyles}
                              />
                              <IconButton
                                aria-label="Delete image"
                                icon={<Trash2 size={14} />}
                                size="xs"
                                onClick={() => removeAt(index)}
                                color="white"
                                bg="red.500"
                                _hover={{ bg: "red.600" }}
                                borderRadius={cardsBorderRadii}
                              />
                            </HStack>
                          </Box>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                )}

                <HStack mt={3} spacing={2} flexWrap="wrap">
                  <Badge colorScheme="blue">{imageCount} image(s)</Badge>
                  {previews[0] && (
                    <Badge colorScheme="green">First image = cover</Badge>
                  )}
                  {isUploading && (
                    <Badge colorScheme="teal">Uploading...</Badge>
                  )}
                  {saving && <Badge colorScheme="purple">Saving...</Badge>}
                </HStack>

                {(isUploading || saving) && (
                  <HStack mt={2}>
                    <GlobalSpinner />
                    <Text fontSize="sm" color={theme.textMuted}>
                      {isUploading
                        ? "Uploading selected images to Firebase..."
                        : "Saving product..."}
                    </Text>
                  </HStack>
                )}
              </Box>
            </SimpleGrid>

            <Divider borderColor={theme.border} />

            <Flex
              justify="space-between"
              align={{ base: "flex-start", md: "center" }}
              direction={{ base: "column", md: "row" }}
              gap={3}
            >
              <Box>
                <Heading size="sm" color={theme.text}>
                  Product Options
                </Heading>
                <Text color={theme.textMuted} fontSize="sm" mt={1}>
                  Optional groups like Color, Size, Material. Leave empty for
                  products without options.
                </Text>
              </Box>

              <Button
                leftIcon={<Plus size={14} />}
                onClick={addVariantAttribute}
                size="sm"
                {...secondaryButtonStyles}
              >
                Add Option Group
              </Button>
            </Flex>

            {draft.variantAttributes.length === 0 ? (
              <Box
                border={`1px dashed ${theme.border}`}
                borderRadius={cardsBorderRadii}
                p={5}
                bg={theme.surfaceAlt}
              >
                <Text color={theme.textMuted} fontSize="sm">
                  This product has no option groups. That is valid and will
                  still update correctly.
                </Text>
              </Box>
            ) : (
              <VStack spacing={4} align="stretch">
                {draft.variantAttributes.map((attribute, attributeIndex) => (
                  <Box
                    key={`attribute-${attributeIndex}`}
                    border={`1px solid ${theme.border}`}
                    borderRadius={cardsBorderRadii}
                    p={4}
                    bg={theme.surfaceAlt}
                  >
                    <Flex justify="space-between" align="center" mb={4}>
                      <HStack spacing={2}>
                        <Badge colorScheme="green">
                          Group {attributeIndex + 1}
                        </Badge>
                        <Badge colorScheme="blue">
                          {(attribute.options || []).length} option(s)
                        </Badge>
                      </HStack>

                      <IconButton
                        aria-label="Remove option group"
                        icon={<Trash2 size={14} />}
                        size="sm"
                        onClick={() => removeVariantAttribute(attributeIndex)}
                        {...secondaryButtonStyles}
                      />
                    </Flex>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                      <FormControl isRequired>
                        <FormLabel color={theme.textMuted}>
                          Group Name
                        </FormLabel>
                        <Input
                          value={attribute.name}
                          onChange={(e) =>
                            updateVariantAttribute(
                              attributeIndex,
                              "name",
                              e.target.value,
                            )
                          }
                          placeholder="e.g. Color or Size"
                          {...baseControlStyle}
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel color={theme.textMuted}>
                          Group Type
                        </FormLabel>
                        <Select
                          value={attribute.type}
                          onChange={(e) =>
                            updateVariantAttribute(
                              attributeIndex,
                              "type",
                              e.target.value as VariantAttributeDraft["type"],
                            )
                          }
                          {...baseControlStyle}
                          sx={{
                            option: {
                              background: isDark ? "#1f2430" : "#fff",
                              color: isDark ? "#f7fafc" : "#1a202c",
                            },
                          }}
                        >
                          <option value="text">Text</option>
                          <option value="color">Color</option>
                          <option value="size">Size</option>
                        </Select>
                      </FormControl>
                    </SimpleGrid>

                    <VStack spacing={3} align="stretch">
                      {attribute.options.map((option, optionIndex) => {
                        const pending =
                          pendingOptionUploads[
                            makeOptionKey(attributeIndex, optionIndex)
                          ];

                        return (
                          <Box
                            key={`option-${attributeIndex}-${optionIndex}`}
                            border={`1px solid ${theme.border}`}
                            borderRadius={cardsBorderRadii}
                            p={3}
                            bg={theme.surface}
                          >
                            <Flex justify="space-between" align="center" mb={3}>
                              <Text
                                color={theme.text}
                                fontWeight="700"
                                fontSize="sm"
                              >
                                Option {optionIndex + 1}
                              </Text>

                              <HStack>
                                <IconButton
                                  aria-label="Add option"
                                  icon={<Plus size={14} />}
                                  size="sm"
                                  onClick={() =>
                                    addVariantOption(attributeIndex)
                                  }
                                  {...secondaryButtonStyles}
                                />
                                <IconButton
                                  aria-label="Remove option"
                                  icon={<Minus size={14} />}
                                  size="sm"
                                  onClick={() =>
                                    removeVariantOption(
                                      attributeIndex,
                                      optionIndex,
                                    )
                                  }
                                  {...secondaryButtonStyles}
                                />
                              </HStack>
                            </Flex>

                            <SimpleGrid
                              columns={{ base: 1, md: 2 }}
                              spacing={3}
                            >
                              <FormControl>
                                <FormLabel color={theme.textMuted}>
                                  Label
                                </FormLabel>
                                <Input
                                  value={option.label}
                                  onChange={(e) =>
                                    updateVariantOption(
                                      attributeIndex,
                                      optionIndex,
                                      "label",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="e.g. Beige, Navy, 32"
                                  {...baseControlStyle}
                                />
                              </FormControl>

                              <FormControl>
                                <FormLabel color={theme.textMuted}>
                                  Value
                                </FormLabel>
                                <Input
                                  value={option.value}
                                  onChange={(e) =>
                                    updateVariantOption(
                                      attributeIndex,
                                      optionIndex,
                                      "value",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Internal value"
                                  {...baseControlStyle}
                                />
                              </FormControl>

                              <FormControl>
                                <FormLabel color={theme.textMuted}>
                                  Hex Color
                                </FormLabel>
                                <Input
                                  value={option.hex}
                                  onChange={(e) =>
                                    updateVariantOption(
                                      attributeIndex,
                                      optionIndex,
                                      "hex",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="#C2B280"
                                  {...baseControlStyle}
                                />
                              </FormControl>

                              <FormControl>
                                <FormLabel color={theme.textMuted}>
                                  Option Price
                                </FormLabel>
                                <Input
                                  type="number"
                                  value={option.price}
                                  onChange={(e) =>
                                    updateVariantOption(
                                      attributeIndex,
                                      optionIndex,
                                      "price",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Optional override"
                                  {...baseControlStyle}
                                />
                              </FormControl>

                              <FormControl>
                                <FormLabel color={theme.textMuted}>
                                  Option Stock Qty
                                </FormLabel>
                                <Input
                                  type="number"
                                  value={option.stockQty}
                                  onChange={(e) =>
                                    updateVariantOption(
                                      attributeIndex,
                                      optionIndex,
                                      "stockQty",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Optional stock"
                                  {...baseControlStyle}
                                />
                              </FormControl>

                              <FormControl>
                                <FormLabel color={theme.textMuted}>
                                  SKU
                                </FormLabel>
                                <Input
                                  value={option.sku}
                                  onChange={(e) =>
                                    updateVariantOption(
                                      attributeIndex,
                                      optionIndex,
                                      "sku",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Optional SKU"
                                  {...baseControlStyle}
                                />
                              </FormControl>

                              <FormControl>
                                <FormLabel color={theme.textMuted}>
                                  Active
                                </FormLabel>
                                <Select
                                  value={option.isActive ? "true" : "false"}
                                  onChange={(e) =>
                                    updateVariantOption(
                                      attributeIndex,
                                      optionIndex,
                                      "isActive",
                                      e.target.value === "true",
                                    )
                                  }
                                  {...baseControlStyle}
                                >
                                  <option value="true">Yes</option>
                                  <option value="false">No</option>
                                </Select>
                              </FormControl>

                              <FormControl gridColumn={{ md: "1 / span 2" }}>
                                <FormLabel color={theme.textMuted}>
                                  Option Images
                                </FormLabel>

                                <Button
                                  as="label"
                                  size="sm"
                                  leftIcon={<Upload size={14} />}
                                  {...secondaryButtonStyles}
                                  cursor="pointer"
                                >
                                  Upload images
                                  <Input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    display="none"
                                    onChange={(e) =>
                                      handleOptionImagesPicked(
                                        attributeIndex,
                                        optionIndex,
                                        e.target.files,
                                      )
                                    }
                                  />
                                </Button>

                                {(option.images.length > 0 ||
                                  (pending?.previews?.length || 0) > 0) && (
                                  <Wrap mt={3} spacing={2}>
                                    {option.images.map((src, imgIdx) => (
                                      <WrapItem key={`${src}-${imgIdx}`}>
                                        <Box position="relative">
                                          <Image
                                            src={src}
                                            alt={`${option.label || option.value}-${imgIdx}`}
                                            boxSize="60px"
                                            objectFit="cover"
                                            borderRadius={cardsBorderRadii}
                                            border={`1px solid ${theme.border}`}
                                          />
                                          <IconButton
                                            aria-label="Remove saved option image"
                                            icon={<Trash2 size={12} />}
                                            size="xs"
                                            position="absolute"
                                            top="-6px"
                                            right="-6px"
                                            onClick={() =>
                                              removeSavedOptionImage(
                                                attributeIndex,
                                                optionIndex,
                                                imgIdx,
                                              )
                                            }
                                            color="white"
                                            bg="red.500"
                                            _hover={{ bg: "red.600" }}
                                            borderRadius={cardsBorderRadii}
                                          />
                                        </Box>
                                      </WrapItem>
                                    ))}

                                    {(pending?.previews || []).map(
                                      (src, imgIdx) => (
                                        <WrapItem key={`${src}-${imgIdx}`}>
                                          <Box position="relative">
                                            <Image
                                              src={src}
                                              alt={`pending-${option.label || option.value}-${imgIdx}`}
                                              boxSize="60px"
                                              objectFit="cover"
                                              borderRadius={cardsBorderRadii}
                                              border={`1px solid ${theme.border}`}
                                              opacity={0.9}
                                            />
                                            <Badge
                                              position="absolute"
                                              left="4px"
                                              bottom="4px"
                                              colorScheme="teal"
                                            >
                                              New
                                            </Badge>
                                            <IconButton
                                              aria-label="Remove pending option image"
                                              icon={<Trash2 size={12} />}
                                              size="xs"
                                              position="absolute"
                                              top="-6px"
                                              right="-6px"
                                              onClick={() =>
                                                removePendingOptionImage(
                                                  attributeIndex,
                                                  optionIndex,
                                                  imgIdx,
                                                )
                                              }
                                              color="white"
                                              bg="red.500"
                                              _hover={{ bg: "red.600" }}
                                              borderRadius={cardsBorderRadii}
                                            />
                                          </Box>
                                        </WrapItem>
                                      ),
                                    )}
                                  </Wrap>
                                )}
                              </FormControl>
                            </SimpleGrid>
                          </Box>
                        );
                      })}
                    </VStack>
                  </Box>
                ))}
              </VStack>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button
            onClick={onClose}
            isDisabled={saving || isUploading}
            {...secondaryButtonStyles}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            isLoading={saving}
            loadingText={mode === "edit" ? "Updating..." : "Creating..."}
            {...primaryButtonStyles}
          >
            {mode === "edit" ? "Update Product" : "Create Product"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
